import React, { useRef } from 'react';
import { Box, Skeleton } from '@landr/maestro';
import { Classifier } from 'types/generated/graphql';
import { useMatch } from 'react-router-dom';
import { RoutePaths } from 'types/enums';
import { SearchableClassifier } from './components/SearchableClassifier';
import ClassifiersProvider, {
    useClassifiersContext,
} from './context/ClassifiersContext';
import { PopoverClassifiers } from './components/PopoverClassifiers';

const maxVisible = 20;

type PackClassifierRule = 'hide-on-route' | 'force-visible' | 'force-hidden';

export const useClassifiersText = ({
    classifiers,
    packClassifierRule,
}: {
    classifiers: Classifier[];
    packClassifierRule: PackClassifierRule;
    containerRef: React.RefObject<HTMLDivElement>;
}) => {
    const isPackClassifierHiddenOnRoute =
        packClassifierRule === 'hide-on-route';
    const isPackClassifierRemoved =
        isPackClassifierHiddenOnRoute &&
        !!useMatch(`/${RoutePaths.Packs}/:slug`);

    let filteredClassifiers;

    switch (true) {
        case isPackClassifierRemoved:
        case packClassifierRule === 'force-hidden':
            filteredClassifiers = classifiers.filter(
                (classifier) => !classifier.isPackClassifier,
            );
            break;
        default:
            filteredClassifiers = classifiers;
    }

    const base = filteredClassifiers.slice(0, maxVisible);

    return {
        base,
    };
};

const ClassifiersTextReady = ({
    classifiers,
    isSearchEnabled,
    packClassifierRule,
    showExtra,
}: {
    classifiers: Classifier[];
    showExtra: boolean;
    isSearchEnabled: boolean;
    packClassifierRule: PackClassifierRule;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const { base } = useClassifiersText({
        classifiers,
        packClassifierRule,
        containerRef,
    });

    return (
        <ClassifiersProvider containerRef={containerRef}>
            <ClassifiersTextContent
                base={base}
                isSearchEnabled={isSearchEnabled}
                containerRef={containerRef}
                showExtra={showExtra}
            />
        </ClassifiersProvider>
    );
};

const ClassifiersTextContent = ({
    base,
    isSearchEnabled,
    containerRef,
    showExtra,
}: {
    base: Classifier[];
    isSearchEnabled: boolean;
    containerRef: React.RefObject<HTMLDivElement>;
    showExtra: boolean;
}) => {
    const { hiddenCount, hiddenClassifiers, isClassifierHidden } =
        useClassifiersContext();

    return (
        <Box
            ref={containerRef}
            fontSize={{ base: 'sm', sm: 'xs' }}
            style={{
                position: 'relative',
                overflow: showExtra ? 'visible' : 'hidden',
                display: 'inline-block',
                flexDirection: 'row',
                flexWrap: 'wrap',
                width: '100%',
                height: 'md',
                fontWeight: 'regular',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            }}
        >
            {base?.map((classifier, index) => {
                const isHidden = isClassifierHidden(classifier.id);
                const isLastVisible =
                    !isHidden &&
                    base
                        .slice(index + 1)
                        .every((c) => isClassifierHidden(c.id));

                return (
                    <React.Fragment key={classifier.code}>
                        <SearchableClassifier
                            classifier={classifier}
                            isSearchEnabled={isSearchEnabled}
                        />
                        {isLastVisible && hiddenCount > 0 && showExtra && (
                            <PopoverClassifiers
                                classifiers={hiddenClassifiers}
                                isSearchEnabled={isSearchEnabled}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export const ClassifiersText = ({
    classifiers,
    isLoading = false,
    showExtra = true,
    isSearchEnabled = true,
    packClassifierRule = 'hide-on-route',
}: {
    classifiers: Classifier[];
    isLoading?: boolean;
    showExtra?: boolean;
    isSearchEnabled?: boolean;
    packClassifierRule?: PackClassifierRule;
}) => {
    if (isLoading) {
        return (
            <Skeleton
                aria-busy="true"
                width="sm"
                height="14px"
                marginRight="2xs"
            />
        );
    }

    return (
        <ClassifiersTextReady
            classifiers={classifiers}
            showExtra={showExtra}
            isSearchEnabled={isSearchEnabled}
            packClassifierRule={packClassifierRule}
        />
    );
};
